import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';

import ContentContainer from '../../common/ContentContainer';
import BlockHeader from '../../common/BlockHeader';

import IsleIcon from '../../../assets/clan/clanStats/isle.svg';
import IsleEmptyIcon from '../../../assets/clan/clanStats/isle_empty.svg';
import WinIcon from '../../../assets/clan/clanStats/win.svg';
import WinEmptyIcon from '../../../assets/clan/clanStats/win_empty.svg';
import PvPIcon from '../../../assets/clan/clanStats/pvp.svg';
import PvPWLKNIcon from '../../../assets/common/icons/wlkn_coin24.svg';
import PvPWLKNEmptyIcon from '../../../assets/clan/clanStats/pvp_empty.svg';
import { useDataState } from '../../../providers/DataProvider.hooks';
import { BarData } from '../../../providers/DataProvider.types';

const Bar = ({
    data,
    color,
    image,
    emptyImage,
    format,
}: {
    data: BarData[] | undefined;
    color: string;
    image: string;
    emptyImage: string;
    format?: (val: number) => number | string;
}) => {
    const isMobile = useMediaQuery('(max-width:1200px)');
    const [growCooef, setGrowCooef] = useState(0);
    const maxBlockHeight = 264;
    const highestElement = useMemo(() => (data ? [...data].sort((a, b) => b.value - a.value)[0].value : 0), [data]);
    const heightCooef = highestElement / maxBlockHeight;

    useEffect(() => {
        if (!data) return;
        setTimeout(() => {
            setGrowCooef(1);
        }, 0);
    }, [data, highestElement]);

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '20px',
                alignItems: 'flex-end',
            }}
        >
            {data?.map((el, index) => (
                <Box
                    key={index}
                    sx={{
                        width: '100%',
                    }}
                >
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'center'}
                        margin={'auto'}
                        gap={'5px'}
                        paddingBottom={'5px'}
                    >
                        <img
                            style={{
                                width: '24px',
                                height: '24px',
                            }}
                            src={el.value > 0 ? image : emptyImage}
                            alt="coin"
                        ></img>
                        <Typography
                            sx={{
                                color: '#0F0E0E',
                                fontFamily: 'AvenirNext500',
                                fontSize: isMobile ? '14px' : '16px',
                                opacity: el.value > 0 ? 1 : 0.4,
                            }}
                        >
                            {format ? format(el.value) : el.value}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            borderTopLeftRadius: '10px',
                            borderTopRightRadius: '10px',
                            width: '100%',
                            height: `${(el.value / heightCooef) * growCooef}px`,
                            backgroundColor: color,
                            transition: 'all ease-out 0.3s',
                        }}
                    ></Box>
                    <Box
                        sx={{
                            textAlign: 'center',
                            paddingTop: '5px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#0F0E0E',
                                fontFamily: 'AvenirNext400',
                                fontSize: isMobile ? '12px' : '14px',
                                opacity: 0.4,
                            }}
                        >
                            {el.week}
                        </Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export const LastTournamentStats = () => {
    const { clanTournamentStats } = useDataState();
    const [value, setValue] = useState(0);

    return (
        <ContentContainer colorBg>
            <BlockHeader text="Last Tournaments Clan Stats"></BlockHeader>
            <Box
                sx={{
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    padding: '30px',
                    marginBottom: '60px',
                    marginTop: '20px',
                    height: '354px',
                    display: 'flex',
                    gap: '40px',
                }}
            >
                <Box
                    display={'flex'}
                    flexDirection={'column'}
                    minWidth={254}
                    height={'100%'}
                    justifyContent={'space-between'}
                >
                    <Button
                        startIcon={<img src={IsleIcon} alt="isle" />}
                        className={value === 0 ? 'selecled_value' : undefined}
                        sx={{
                            borderRadius: '20px',
                            border: value === 0 ? '2px solid #F1564C' : '1px solid #ECF0F4',
                            color: '#0F0E0E',
                            fontFamily: 'AvenirNext600',
                            fontSize: '20px',
                            lineHeight: 'normal',
                            padding: '30px 20px',
                            textTransform: 'none',
                            transition: 'all ease 0.3s',
                            gap: '10px',
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            backgroundColor: value === 0 ? 'rgba(241, 86, 76, 0.10)' : 'transparent',
                            '&:hover':
                                value !== 0
                                    ? {
                                          backgroundColor: 'transparent',
                                          border: '1px solid #D8DFE5',
                                      }
                                    : {
                                          backgroundColor: 'rgba(241, 86, 76, 0.10)',
                                      },
                        }}
                        onClick={() => {
                            setValue(0);
                        }}
                    >
                        Clash wins
                    </Button>
                    <Button
                        className={value === 1 ? 'selecled_value' : undefined}
                        startIcon={<img src={WinIcon} alt="win" />}
                        sx={{
                            borderRadius: '20px',
                            border: value === 1 ? '2px solid #FC8B46' : '1px solid #ECF0F4',
                            color: '#0F0E0E',
                            fontFamily: 'AvenirNext600',
                            fontSize: '20px',
                            lineHeight: 'normal',
                            padding: '30px 20px',
                            textTransform: 'none',
                            transition: 'all ease 0.3s',
                            gap: '10px',
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            backgroundColor: value === 1 ? 'rgba(252, 139, 70, 0.10)' : 'transparent',
                            '&:hover':
                                value !== 1
                                    ? {
                                          backgroundColor: 'transparent',
                                          border: '1px solid #D8DFE5',
                                      }
                                    : {
                                          backgroundColor: 'rgba(252, 139, 70, 0.10)',
                                      },
                        }}
                        onClick={() => {
                            setValue(1);
                        }}
                    >
                        Win rate
                    </Button>
                    <Button
                        className={value === 2 ? 'selecled_value' : undefined}
                        startIcon={<img src={PvPIcon} alt="pvp" />}
                        sx={{
                            borderRadius: '20px',
                            border: value === 2 ? '2px solid #FDBB53' : '1px solid #ECF0F4',
                            color: '#0F0E0E',
                            fontFamily: 'AvenirNext600',
                            fontSize: '20px',
                            lineHeight: 'normal',
                            padding: '30px 20px',
                            textTransform: 'none',
                            transition: 'all ease 0.3s',
                            gap: '10px',
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            backgroundColor: value === 2 ? 'rgba(253, 187, 83, 0.10)' : 'transparent',
                            '&:hover':
                                value !== 2
                                    ? {
                                          backgroundColor: 'transparent',
                                          border: '1px solid #D8DFE5',
                                      }
                                    : {
                                          backgroundColor: 'rgba(253, 187, 83, 0.10)',
                                      },
                        }}
                        onClick={() => {
                            setValue(2);
                        }}
                    >
                        PvP Earnings
                    </Button>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        justifyContent: 'flex-end',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            maxHeight: '310px',
                            justifyContent: 'space-between',
                            display: 'flex',
                            flexDirection: 'column',
                            marginBottom: '25px',
                            '& div': {
                                borderTop: '1px solid #ECF0F4',
                                width: '100%',
                            },
                        }}
                    >
                        <Box />
                        <Box />
                        <Box />
                        <Box />
                        <Box />
                    </Box>
                    <Box
                        sx={{
                            zIndex: 1,
                        }}
                    >
                        {value === 0 && (
                            <Bar
                                image={IsleIcon}
                                emptyImage={IsleEmptyIcon}
                                data={clanTournamentStats?.wins}
                                color="#F1564C"
                            />
                        )}
                        {value === 1 && (
                            <Bar
                                image={WinIcon}
                                emptyImage={WinEmptyIcon}
                                data={clanTournamentStats?.win_rate}
                                color="#FC8B46"
                                format={(value: number) => {
                                    return `${value.toFixed(2)}%`;
                                }}
                            />
                        )}
                        {value === 2 && (
                            <Bar
                                image={PvPWLKNIcon}
                                emptyImage={PvPWLKNEmptyIcon}
                                data={clanTournamentStats?.pvp}
                                color="#FDBB53"
                                format={(value: number) => {
                                    return value.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    });
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </ContentContainer>
    );
};
